import { useMemo } from 'react'
import { fmtCompact } from './format'
import type { TrendPoint, TrendView } from '../types'

interface TrendChartProps {
  trend: TrendPoint[]
  view: TrendView
  currency: string
}

interface Series {
  name: string
  color: string
  dashed?: boolean
  points: (number | null)[]
}

const TAN = '#AE9056'
const SLATE = '#5E6E8C'
const W = 780
const H = 248
const PAD_L = 14
const PAD_R = 18
const PAD_T = 14
const PAD_B = 26

const CAL = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

function buildSeries(trend: TrendPoint[], view: TrendView): { xLabels: string[]; series: Series[] } {
  if (view === 'priorYear') {
    const pick = (suffix: string) =>
      CAL.map((m) => trend.find((t) => t.month === `${m}-${suffix}`)?.afterCN ?? null)
    return {
      xLabels: CAL,
      series: [
        { name: '2025', color: SLATE, dashed: true, points: pick('25') },
        { name: '2026', color: TAN, points: pick('26') },
      ],
    }
  }
  const key: 'gross' | 'afterCN' = view === 'gross' ? 'gross' : 'afterCN'
  return {
    xLabels: trend.map((t) => t.month),
    series: [{ name: view === 'gross' ? 'Gross' : 'After CN', color: TAN, points: trend.map((t) => t[key]) }],
  }
}

/** Hand-rolled SVG line chart for the 17-month invoiced trend. */
export function TrendChart({ trend, view, currency }: TrendChartProps) {
  const { xLabels, series } = useMemo(() => buildSeries(trend, view), [trend, view])

  const all = series.flatMap((s) => s.points).filter((v): v is number => v != null)
  const max = all.length ? Math.max(...all) : 1
  const min = all.length ? Math.min(...all) : 0
  const yMax = max * 1.08
  const yMin = Math.max(0, min * 0.88)
  const n = xLabels.length
  const xAt = (i: number) => PAD_L + (i * (W - PAD_L - PAD_R)) / Math.max(1, n - 1)
  const yAt = (v: number) => PAD_T + (H - PAD_T - PAD_B) * (1 - (v - yMin) / (yMax - yMin || 1))
  const grid = [0, 0.25, 0.5, 0.75, 1].map((t) => yMin + t * (yMax - yMin))
  const step = Math.ceil(n / 9)

  const primary = series[series.length - 1]
  const lastIdx = primary.points.map((v, i) => (v != null ? i : -1)).filter((i) => i >= 0).pop() ?? -1

  return (
    <div>
      {series.length > 1 && (
        <div className="mb-2 flex items-center gap-4">
          {series.map((s) => (
            <span key={s.name} className="inline-flex items-center gap-1.5 text-xs text-stone-500 dark:text-stone-400">
              <span
                className="inline-block h-0.5 w-4 rounded"
                style={{ backgroundColor: s.color, opacity: s.dashed ? 0.6 : 1 }}
                aria-hidden
              />
              {s.name}
            </span>
          ))}
        </div>
      )}
      <svg viewBox={`0 0 ${W} ${H}`} className="h-auto w-full" role="img" aria-label="Invoiced trend over time">
        {grid.map((gv, i) => (
          <g key={i}>
            <line
              x1={PAD_L}
              x2={W - PAD_R}
              y1={yAt(gv)}
              y2={yAt(gv)}
              className="stroke-stone-200 dark:stroke-stone-800"
              strokeWidth={1}
            />
            <text x={W - PAD_R} y={yAt(gv) - 3} textAnchor="end" className="fill-stone-400 text-[9px]">
              {fmtCompact(gv, currency)}
            </text>
          </g>
        ))}

        {xLabels.map((lb, i) =>
          i % step === 0 || i === n - 1 ? (
            <text key={i} x={xAt(i)} y={H - 7} textAnchor="middle" className="fill-stone-400 text-[9px]">
              {lb}
            </text>
          ) : null,
        )}

        {series.map((s, si) => {
          const pts = s.points
            .map((v, i) => (v != null ? `${xAt(i)},${yAt(v)}` : null))
            .filter(Boolean)
            .join(' ')
          return (
            <polyline
              key={si}
              points={pts}
              fill="none"
              stroke={s.color}
              strokeWidth={2}
              strokeDasharray={s.dashed ? '4 4' : undefined}
              strokeLinejoin="round"
              strokeLinecap="round"
              opacity={s.dashed ? 0.7 : 1}
            />
          )
        })}

        {lastIdx >= 0 && primary.points[lastIdx] != null && (
          <circle cx={xAt(lastIdx)} cy={yAt(primary.points[lastIdx] as number)} r={3.5} fill={primary.color} />
        )}
      </svg>
    </div>
  )
}
